import { useMemo } from 'react'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { usePollAISuggestionRequest } from 'hooks/queries/aiSuggestionRequests/usePollAISuggestionRequest'
import { useVisualStyles } from 'hooks/queries/visualStyles'
import { useFontPairings } from 'hooks/queries/fontPairings'

interface RecommendationResult {
  visual_style_ids?: number[]
  font_pairing_ids?: number[]
}

export const useDisplayedFontPairingsAndVisualStyles = (brandKitId?: number) => {
  const { data: brandKitData, isLoading: isLoadingBrandKit } = useBrandKit(brandKitId, {
    includeBrandVisualAnalysis: true,
    enabled: !!brandKitId,
    refetchInterval: (data) => {
      const requestId =
        data?.brand_kit?.brand_visual_kit?.brand_visual_analysis?.result?.recommend_styles_and_fonts_request_id
      return requestId ? false : 1000
    },
  })

  const brandVisualKit = brandKitData?.brand_kit?.brand_visual_kit
  const brandVisualAnalysis = brandVisualKit?.brand_visual_analysis
  const recommendRequestId = brandVisualAnalysis?.result?.recommend_styles_and_fonts_request_id

  const analysisIsRunning =
    !!brandVisualKit?.brand_visual_analysis_id &&
    (!brandVisualAnalysis || brandVisualAnalysis.status === 'pending' || brandVisualAnalysis.status === 'started')
  const analysisSucceededWithoutRequestId = brandVisualAnalysis?.status === 'successful' && !recommendRequestId
  const isWaitingForRequestId = analysisIsRunning || analysisSucceededWithoutRequestId

  const { data: recommendationData, isLoading: isLoadingRecommendation } = usePollAISuggestionRequest(
    { id: recommendRequestId || 0 },
    { enabled: !!recommendRequestId },
  )

  const recommendationResult = useMemo((): RecommendationResult | undefined => {
    if (!recommendationData?.request?.generated_content) return undefined
    try {
      return JSON.parse(recommendationData.request.generated_content) as RecommendationResult
    } catch {
      return undefined
    }
  }, [recommendationData?.request?.generated_content])

  const { data: visualStylesData } = useVisualStyles()
  const { data: fontPairingsData } = useFontPairings()

  const displayedVisualStyles = useMemo(() => {
    if (!visualStylesData || visualStylesData.length === 0) return []

    const recommendedIds = recommendationResult?.visual_style_ids ?? []

    const recommended = recommendedIds
      .map((id) => visualStylesData.find((s) => s.id === id))
      .filter((s): s is NonNullable<typeof s> => !!s)
      .slice(0, 3)

    if (recommended.length === 3 || !recommendedIds.length) return recommended // if we have 3 recommended (got them all) or no recommended (still polling), return the recommended

    const resolvedIds = new Set(recommended.map((s) => s.id))
    const fallbacks = visualStylesData.filter((s) => !resolvedIds.has(s.id))
    return [...recommended, ...fallbacks].slice(0, 3)
  }, [visualStylesData, recommendationResult])

  const displayedFontPairings = useMemo(() => {
    if (!fontPairingsData || fontPairingsData.length === 0) return []

    const recommendedIds = recommendationResult?.font_pairing_ids ?? []
    const recommended = recommendedIds
      .map((id) => fontPairingsData.find((fp) => fp.id === id))
      .filter((fp): fp is NonNullable<typeof fp> => !!fp)
      .slice(0, 3)

    if (recommended.length === 3 || !recommendedIds.length) return recommended // if we have 3 recommended (got them all) or no recommended (still polling), return the recommended

    const resolvedIds = new Set(recommended.map((fp) => fp.id))
    const fallbacks = fontPairingsData.filter((fp) => !resolvedIds.has(fp.id))
    return [...recommended, ...fallbacks].slice(0, 3)
  }, [fontPairingsData, recommendationResult])

  const isLoading = isLoadingBrandKit || isWaitingForRequestId || isLoadingRecommendation

  return {
    displayedFontPairings,
    displayedVisualStyles,
    isLoading,
    brandKitData,
    brandVisualKitId: brandVisualKit?.id,
  }
}
